import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DATE_TIME_FORMAT } from '@common/constants';
import { catchError, map, Observable } from 'rxjs';
import * as moment from 'moment/moment';
import {
  IEventAgenda,
  IEventResolution,
  IProtocol,
  IProtocolData,
  PageModel,
  IProtocolDocData,
  IProtocolWrapper,
  IProtocolRequest,
  IBlackListBody
} from '@common/types';
import { compareByField } from '@common/utils/util';
import { FileService } from '@common/services/file.service';
import { VoteEnum } from '@common/enums';

@Injectable({
  providedIn: 'root'
})
export class ProtocolService {
  constructor(
    private readonly _http: HttpClient,
    private readonly _fileService: FileService
  ) {}

  // ***** Protocol Event Agenda Item *****
  /**
   * Get all agendas for event protocol
   *
   * @param eventId
   * @returns
   */
  public retrieveProtocolEventAgendas(
    eventId: string
  ): Observable<IEventAgenda[]> {
    return this._http.get(`api/ProtocolEventAgendaItem/${eventId}/agenda`).pipe(
      map((agendaItems: IEventAgenda[]) => {
        return agendaItems.sort(compareByField('order')).map((agendaItem) => ({
          ...agendaItem,
          materials: agendaItem.materials.map((material) => ({
            ...material,
            created: moment(material.created).format()
          }))
        }));
      })
    );
  }

  //TODO проверить на беке нет такого ендпоинта
  /**
   * Create protocol event agendas
   *
   * @param eventId
   * @param body
   * @returns
   */
  public createProtocolEventAgenda(
    eventId: string,
    body: IEventAgenda
  ): Observable<IEventAgenda> {
    return this._http.post<IEventAgenda>(
      `api/ProtocolEventAgendaItem/${eventId}/agenda`,
      body
    );
  }

  //TODO проверить на беке нет такого ендпоинта
  /**
   * Update agendas for event protocol
   *
   * @param eventId
   * @param body
   * @returns
   */
  public updateProtocolEventAgenda(eventId: string, body: IEventAgenda): Observable<IEventAgenda> {
    return this._http.put<IEventAgenda>(`api/ProtocolEventAgendaItem/${eventId}/agenda`, body).pipe(
      map((res: IEventAgenda) => ({
        ...res,
        materials: res.materials.map((material) => ({
          ...material,
          created: moment(material.created).format()
        }))
      }))
    );
  }
  //TODO перенести в agenda-item-attachment.service.ts
  // ***** Agenda Item Attachment *****
  /**
   * Upadate black list of material
   *
   * @param attachmentId
   * @param type
   * @param employeeList
   * @returns
   */
  public updateBlackList(body: IBlackListBody): Observable<void> {
    return this._http.put<void>('api/AgendaItemAttachment/UpdateBlackList', body);
  }

  // ***** Protocol Event Resolution *****
  /**
   * Create One or More protocol resolution
   *
   * @param body
   * @returns
   */
  public createSomeProtocolEventResolutions(
    body: Partial<IEventResolution>[]
  ): Observable<Partial<IEventResolution>[]> {
    return this._http.post<Partial<IEventResolution>[]>('api/ProtocolEventResolution/batch', body);
  }
  /**
   * Create One protocol resolution
   *
   * @param body
   * @returns
   */
  public createProtocolEventResolution(body: Partial<IEventResolution>): Observable<IEventResolution> {
    return this._http.post<IEventResolution>('api/ProtocolEventResolution', body);
  }
  /**
   * Update Protocol resolution
   *
   * @param body
   * @returns
   */
  public updateProtocolEventResolution(body: Partial<IEventResolution>): Observable<IEventResolution> {
    return this._http.put<IEventResolution>('api/ProtocolEventResolution', body);
  }
  /**
   * Delete Protocol resolution
   *
   * @param protocolResolutionEventId
   * @param committeeEventId
   * @returns
   */
  public deleteProtocolEventResolution(
    protocolResolutionEventId: string,
    committeeEventId: string
  ): Observable<void> {
    return this._http.patch<void>(`api/ProtocolEventResolution/${protocolResolutionEventId}`, {
      committeeEventId
    });
  }
  /**
   * Отправка события кнопки "Обсуждение повестки"
   *
   * @param eventId
   * @param agendaItemId
   * @returns
   */
  public startProtocolEventAgendaDiscussion(eventId: string, agendaItemId: string): Observable<void> {
    // return this._http.get<IEventAgenda>(`api/ProtocolEventAgendaItem/${eventId}/agenda/${agendaItemId}`);
    return this._http.post<void>('api/ProtocolEventResolution/StartDiscussion', {
      committeeEventId: eventId,
      agendaItemId: agendaItemId
    });
  }

  public setVote(
    committeeEventId: string,
    resolutionId: string,
    employeeId: string,
    vote: VoteEnum
  ): Observable<void> {
    return this._http.put<void>('api/ProtocolEventResolution/SetVote', {
      committeeEventId,
      resolutionId,
      employeeId,
      vote
    });
  }

  public sendProtocolEventRating(
    committeeEventId: string,
    resolutionId: string,
    rating: number
  ): Observable<any> {
    return this._http.patch(`api/ProtocolEventResolution/${resolutionId}/${rating}`, null);
  }

  // ***** Send external api *****

  public sendProtocolWebhook(body: any): Observable<any> {
    return this._http.post('https://n8n.efko.ru/webhook/581b0204-f13a-4ce5-b67d-bf626bc78fc7', body);
  }

  // ***** Protocol *****
  public getProtocolsEventsByCommittee(
    options: IProtocolRequest
  ): Observable<IProtocolDocData> {
    let params = new HttpParams()
      .set('page', options.page || 0)
      .set('pageSize', options.pageSize || 10);

    if (options.startDate) {
      params = params.append('startDate', options.startDate);
    }

    if (options.endDate) {
      params = params.append('endDate', options.endDate);
    }

    return this._http.get(`api/ProtocolEvent/committee/${options.committeeId}`, { params }).pipe(
      map((res: IProtocolWrapper) => ({
        data: res.data.map((protocol) => ({
          ...protocol,
          committeeEventDto: {
            ...protocol.committeeEventDto,
            eventTime: moment.utc(protocol.committeeEventDto.eventTime).local().format(DATE_TIME_FORMAT)
          }
        })),
        pagination: new PageModel(res)
      }))
    );
  }

  public getProtocolPdf(eventId: string, filename: string = 'file'): void {
    this._http
      .get(`api/ProtocolEvent/pdf/${eventId}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        catchError((err) => {
          throw new Error(`Can't download. Details: ${err}`);
        })
      )
      .subscribe((res) => {
        this._fileService.saveFile(res.body, filename, 'application/pdf');
      });
  }

  public getProtocolZIP(eventId: string): Observable<HttpResponse<Blob>> {
    return this._http
      .get(`api/protocolEvent/downloadZip/${eventId}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        map((res) => {
          if (res.status === 204) {
            throw new Error('File is empty');
          }
          return res;
        })
      );
  }

  public getProtocolSinglePdf(eventId: string): Observable<HttpResponse<Blob>> {
    return this._http
      .get(`api/protocolEvent/getMergedAttachmentPDFFiles/${eventId}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        map((res) => {
          if (res.status === 204) {
            throw new Error('File is empty');
          }
          return res;
        })
      );
  }
}
